/**
 * CriterionVerification Component
 *
 * Compact inline component for human verification of each criterion result.
 * Allows reviewers to agree with AI or override with their own determination.
 */

import { useState } from 'react';
import { CheckCircle, Edit2, Save } from 'lucide-react';
import type { CriterionResult, VerificationPayload, ResultType } from '../../types';
import { RESULT_TYPE_LABELS } from '../../types';
import { OverrideReasonModal } from './OverrideReasonModal';

interface CriterionVerificationProps {
  result: CriterionResult;
  onVerify: (payload: VerificationPayload) => Promise<void>;
  disabled?: boolean;
}

type VerificationChoice = 'agree' | 'override';

const RESULT_OPTIONS: Array<{ value: ResultType; label: string }> = [
  { value: 'met', label: 'Met' },
  { value: 'not_met', label: 'Not Met' },
  { value: 'uncertain', label: 'Uncertain' },
];

export function CriterionVerification({
  result,
  onVerify,
  disabled = false,
}: CriterionVerificationProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [choice, setChoice] = useState<VerificationChoice | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);

  // Already verified - show verification status
  if (result.human_verified && !isEditing) {
    const hasOverride = result.human_result && result.human_result !== result.result;

    return (
      <div className="flex items-center gap-3 py-2">
        <div className="flex items-center gap-2">
          <CheckCircle size={16} className="text-green-600 dark:text-green-400" />
          <span className="text-sm text-green-700 dark:text-green-300 font-medium">Verified</span>
        </div>

        {hasOverride && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-400 dark:text-gray-500">|</span>
            <span className="text-gray-600 dark:text-gray-400">
              Override: <span className="font-medium text-amber-700 dark:text-amber-300">
                {RESULT_TYPE_LABELS[result.human_result!]}
              </span>
            </span>
            {result.human_notes && (
              <span className="text-gray-500 dark:text-gray-400 italic truncate max-w-xs" title={result.human_notes}>
                - {result.human_notes}
              </span>
            )}
          </div>
        )}

        {!disabled && (
          <button
            onClick={() => setIsEditing(true)}
            className="ml-auto text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1"
          >
            <Edit2 size={14} />
            Edit
          </button>
        )}
      </div>
    );
  }

  // Not yet verified or editing - show verification form
  const handleAgree = async () => {
    setIsSubmitting(true);
    try {
      await onVerify({
        human_result: result.result,
      });
      // Only collapse the form once the write actually succeeded.
      setChoice(null);
      setIsEditing(false);
    } catch {
      // Verification failed — keep the form open so the reviewer can retry.
      // The parent (ReviewResults) surfaces the error toast.
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverrideSubmit = async (newResult: ResultType, reason: string) => {
    setIsSubmitting(true);
    try {
      await onVerify({
        human_result: newResult,
        override_reason: reason,
        notes: reason,
      });
      // Only collapse the form once the write actually succeeded. On failure the
      // rejection propagates (try/finally) so OverrideReasonModal keeps itself
      // open and shows its own error — no explicit catch needed.
      setChoice(null);
      setIsEditing(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setChoice(null);
    setIsEditing(false);
  };

  return (
    <div className="py-2 space-y-3">
      {/* Verification choice */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-600 dark:text-gray-400">Your verification:</span>

        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`verification-${result.id}`}
              checked={choice === 'agree'}
              onChange={() => setChoice('agree')}
              disabled={disabled || isSubmitting}
              className="w-4 h-4 text-primary-600 dark:text-primary-400 border-gray-300 dark:border-gray-600 focus:ring-primary-500 disabled:opacity-50"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Agree with AI</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`verification-${result.id}`}
              checked={choice === 'override'}
              onChange={() => setChoice('override')}
              disabled={disabled || isSubmitting}
              className="w-4 h-4 text-red-600 dark:text-red-400 border-gray-300 dark:border-gray-600 focus:ring-red-500 disabled:opacity-50"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Override AI</span>
          </label>
        </div>
      </div>

      {/* Action buttons based on choice */}
      {choice && (
        <div className="flex items-center gap-2">
          {choice === 'agree' && (
            <button
              onClick={handleAgree}
              disabled={isSubmitting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
            >
              {isSubmitting ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={14} />
                  Save Verification
                </>
              )}
            </button>
          )}

          {choice === 'override' && (
            <button
              onClick={() => setShowOverrideModal(true)}
              disabled={isSubmitting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
            >
              Provide Override Reason
            </button>
          )}

          {(result.human_verified || isEditing) && (
            <button
              onClick={handleCancel}
              disabled={isSubmitting}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 transition-colors min-h-[44px] sm:min-h-0"
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {/* Override reason modal */}
      <OverrideReasonModal
        isOpen={showOverrideModal}
        aiResult={result.result}
        criterionText={result.criterion_text}
        onSubmit={handleOverrideSubmit}
        onClose={() => setShowOverrideModal(false)}
      />
    </div>
  );
}
